import React, { PureComponent } from 'react';
import { withStyles } from '@material-ui/core';
import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { cloneDeep } from 'lodash/fp';
import moment from 'moment';
import numeral from 'numeral';
import uniqid from 'uniqid';
import faker from 'faker';
import PropTypes from 'prop-types';

const timeFormat = (item) => moment(item * 1000).format('MMM DD');
const valueFormat = (item) => numeral(item).format('0,0');

const styles = (theme) => ({
  tooltipUpper: {
    border: 'none !important',
    borderRadius: theme.spacing(0.25),
    backgroundColor: '#9AC802 !important',
    padding: `${theme.spacing(0.25)}px !important`
  },
  tooltipLower: {
    border: 'none !important',
    borderRadius: theme.spacing(0.25),
    backgroundColor: '#FA4F4F !important',
    padding: `${theme.spacing(0.25)}px !important`
  },
  tooltipLabel: {
    display: 'none'
  }
});

class TrendingChart extends PureComponent {
  constructor(props) {
    super(props);
    this.gradient = uniqid();
    this.end = Math.round(new Date().getTime() / 1000); // in seconds
    this.start = Math.round(moment(this.end * 1000).add(-3, 'months').toDate().getTime() / 1000); // in seconds
    this.state = {
      series: this.generateSeries(this.start, this.end, 3600 * 4) // per 4 hours
    };
  }

  generateSeries(start, end, step) {
    const result = [];
    for (let i = start; i < end; i += step) {
      result.push({
        time: i,
        value: faker.datatype.number({ min: 100, max: 900 })
      });
    }
    return result;
  }

  handleSeries() {
    const result = [];
    const series = cloneDeep(this.state.series);
    for (let i = 0; i < series.length; i++) {
      let found = false;
      switch (this.props.duration) {
        case 'day':
          found = series[i].time >= this.end - 86400;
          break;
        case 'week':
          found = series[i].time >= this.end - 86400 * 7;
          break;
        case 'month':
          const start = Math.round(moment(this.end * 1000).add(-1, 'month').toDate().getTime() / 1000);
          found = series[i].time >= start;
          break;
        case 'all':
          found = true;
          break;
        default:
          found = false;
          break;
      }
      if (found) {
        result.push(series[i]);
      }
    }
    let refPoint = null;
    if (result.length > 1) {
      const index = Math.round(result.length * 0.3);
      refPoint = {
        time: result[index].time,
        value: result[index].value
      };
    }
    // decrease all points by refPoint so that refPoint is original, for fillByValue of recharts
    let min = null;
    let max = null;
    if (refPoint) {
      for (let i = 0; i < result.length; i++) {
        result[i].value -= refPoint.value;
        if (min === null) {
          min = result[i].value;
        } else if (min > result[i].value) {
          min = result[i].value;
        }
        if (max === null) {
          max = result[i].value;
        } else if (max < result[i].value) {
          max = result[i].value;
        }
      }
    }
    // detect chart color based on the last value
    const color = result[result.length - 1].value > 0 ? '#9AC802' : '#FA4F4F';
    const tooltipWrapper = result[result.length - 1].value > 0 ? this.props.classes.tooltipUpper : this.props.classes.tooltipLower;
    return { result, refPoint, min, max, color, tooltipWrapper };
  }

  render() {
    const { result, refPoint, min, max, color, tooltipWrapper } = this.handleSeries();
    return (
      <ResponsiveContainer height={this.props.height}>
        <AreaChart data={result}>
          <defs>
            <linearGradient id={this.gradient} x1="0" y1="0" x2="0" y2="1">
              <stop offset={0} stopColor={color} stopOpacity={0.5} />
              <stop offset={max / (max - min)} stopColor={color} stopOpacity={0} />
              <stop offset={max / (max - min)} stopColor={color} stopOpacity={0} />
              <stop offset={1} stopColor={color} stopOpacity={0.5} />
            </linearGradient>
          </defs>
          <XAxis dataKey="time" hide />
          <YAxis hide />
          {refPoint && (
            <ReferenceLine
              x={refPoint.time}
              stroke="rgba(102, 110, 121, 0.5)"
              strokeDasharray="3 3"
            />
          )}
          {refPoint && (
            <ReferenceLine
              y={0}
              stroke="rgba(102, 110, 121, 0.5)"
              strokeDasharray="3 3"
            />
          )}
          <Tooltip
            labelFormatter={timeFormat}
            formatter={valueFormat}
            wrapperClassName={tooltipWrapper}
            labelClassName={this.props.classes.tooltipLabel}
            itemStyle={{
              paddingTop: 0,
              paddingBottom: 0,
              color: '#fff',
              fontWeight: '500',
              fontSize: this.props.theme.spacing(1.125),
              lineHeight: this.props.theme.spacing(1.5) + 'px'
            }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            fill={`url(#${this.gradient})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }
}

TrendingChart.propTypes = {
  height: PropTypes.number.isRequired,
  duration: PropTypes.string.isRequired
};

export default withStyles(styles, { withTheme: true })(TrendingChart);
